import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import remarkGfm from "remark-gfm";
import type { CardAction, ChatMessage } from "../types/chat";
import { ToolActivity } from "./ToolActivity";
import { ActionConfirmation } from "./ActionConfirmation";
import { CardRenderer } from "./cards/CardRenderer";
import { SpeakButton } from "./SpeakButton";

interface MessageBubbleProps {
  message: ChatMessage;
  onConfirm?: () => void;
  onReject?: () => void;
}

export function MessageBubble({ message, onConfirm, onReject }: MessageBubbleProps) {
  const navigate = useNavigate();
  const isUser = message.role === "user";

  const handleCardAction = (action: CardAction) => {
    if (action.action_type === "navigate" && action.payload.path) {
      navigate(action.payload.path as string);
    }
  };

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="px-5 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-[15px] font-medium leading-relaxed bg-muted/60 text-foreground whitespace-pre-wrap break-words">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] space-y-2">
        {/* Tool activity indicators */}
        {(message.toolCalls?.length ?? 0) > 0 && (
          <ToolActivity
            toolCalls={message.toolCalls ?? []}
            toolResults={message.toolResults ?? []}
          />
        )}

        {/* Message text \u2014 transparent per design (weco2/hub.jsx assistant block) */}
        {message.content && (
          <div className="px-1 py-1 text-[15px] leading-relaxed text-foreground break-words assistant-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
            {message.isStreaming && !message.confirmation && (
              <span className="inline-block ml-1 w-1.5 h-4 align-middle bg-foreground animate-pulse" aria-label="Streaming\u2026" />
            )}
          </div>
        )}

        {message.content && !message.isStreaming && (
          <div className="px-1 flex items-center gap-1">
            <SpeakButton messageId={message.id} text={message.content} />
          </div>
        )}

        {/* Generating dots (design spec) when no content yet and no tools running */}
        {!message.content && message.isStreaming && !message.confirmation && (message.toolCalls?.length ?? 0) === 0 && (
          <div className="px-1 py-2 flex gap-1.5 items-center" aria-label="Generating…">
            <span className="w-2 h-2 rounded-full bg-primary/40 animate-pulse" />
            <span className="w-2 h-2 rounded-full bg-primary/60 animate-pulse" style={{ animationDelay: ".15s" }} />
            <span className="w-2 h-2 rounded-full bg-primary/80 animate-pulse" style={{ animationDelay: ".3s" }} />
          </div>
        )}

        {/* Rich cards */}
        {(message.cards?.length ?? 0) > 0 && (
          <CardRenderer cards={message.cards!} onAction={handleCardAction} />
        )}

        {/* Action confirmation card */}
        {message.confirmation && onConfirm && onReject && (
          <ActionConfirmation
            confirmation={message.confirmation}
            onConfirm={onConfirm}
            onReject={onReject}
          />
        )}
      </div>
    </div>
  );
}
